import {inject, Injectable} from '@angular/core';
export const GRID_EDGE_API_BRAND_NAME = 'GridEdge';
import {Endpoints} from "../../models/Endpoints";
import {Api} from '../api/api';
import {map, Observable} from "rxjs";
import {BrandDetails, Loa, SubmittedLoa} from "../../models/Loa";
import {MeterSubmission} from '../../models/LoaSubmission';

@Injectable({
  providedIn: 'root',
})
export class LoaService {

  private api = inject(Api);

  /**
   * Fetches the LOA for a given submission token.
   * Returns either an unsigned `Loa` or an already-`SubmittedLoa` depending on its status.
   * @param token : string - The unique submission token from the route.
   */
  getSubmissionLoa(token: string): Observable<Loa | SubmittedLoa> {
    const url = `${this.api.getUrl(Endpoints.LOA_SUBMISSION_TOKEN)}/${token}`;
    return this.api.get(url).pipe(map(data => {
      const loa = data as Loa | SubmittedLoa;
      return loa;
    }));
  }

  /**
   * Submits a completed LOA, posting the signatory details, meter selections,
   * and supporting documents (including the generated PDF) as multipart form data.
   * @param token : string - The submission token identifying the LOA.
   * @param signatory : { name: string; company: string; jobTitle: string; email: string } - The name, company, job title and email of the signatory.
   * @param meters : MeterSubmission[] - Per-meter acceptance flags and consent dates.
   * @param documents : File[] - Files to attach (uploaded evidence + generated PDF).
   */
  submitLoa(
    token: string,
    signatory: { name: string; company: string; jobTitle: string; email: string },
    meters: MeterSubmission[],
    documents: File[]
  ): Observable<SubmittedLoa> {
    const formData = new FormData();
    formData.append('jsonPayload', JSON.stringify({signatory, meters}));
    documents.forEach(file => {
      formData.append('document', file, file.name);
    });

    const url = `${this.api.getUrl(Endpoints.LOA_SUBMISSION_TOKEN)}/${token}`;
    return this.api.post<SubmittedLoa>(url, formData, 'multipart/form-data');
  }

  /**
   * Type guard that returns `true` when the LOA has already been submitted
   * (i.e. it lacks the `meters` property present on an unsigned `Loa`).
   * @param loa : Loa | SubmittedLoa - The LOA object to check.
   */
  isSubmittedLoa(loa: Loa | SubmittedLoa): loa is SubmittedLoa {
    return !('meters' in loa);
  }

  /**
   * Returns `true` when every meter on the LOA is a renewal, meaning the signatory is
   * re-authorising existing meters rather than adding new ones. Returns `false` when the
   * LOA has no meters, or when at least one meter is new.
   * @param loa : Loa - The unsigned LOA to check.
   */
  isRenewalLoa(loa: Loa): boolean {
    return loa.meters.length > 0 && loa.meters.every(meter => meter.isRenewal);
  }

  /**
   * Returns the brand's primary colour, or the GridEdge default green if none is set.
   * @param brandDetails : BrandDetails | null - The brand configuration, or `null` for GridEdge.
   */
  getBrandPrimaryColour(brandDetails: BrandDetails | null): string {
    if (brandDetails?.primaryColor) {
      return brandDetails?.primaryColor;
    } else {
      return '#3D5A6E';
    }
  }

  /**
   * Returns `true` when the brand name matches the GridEdge brand identifier.
   * @param brandName : string - The brand name string to check.
   */
  isGridEdge(brandName: string): boolean {
    return brandName === GRID_EDGE_API_BRAND_NAME;
  }

  /**
   * Returns the brand's header image URL, or `null` if none is configured.
   * @param brandDetails : BrandDetails | null - The brand configuration, or `null` for GridEdge.
   */
  getBrandHeaderImage(brandDetails: BrandDetails | null): string | null {
    if (brandDetails?.headerImage) {
      return brandDetails?.headerImage;
    } else {
      return null;
    }
  }

  /**
   * Replaces all occurrences of "Grid Edge" in the given content with
   * `"<brandName> and Grid Edge"` for non-GridEdge brands.
   * @param content : string - The raw text content to transform.
   * @param brandName : string - The brand name to inject.
   */
  applyBrandSubstitution(content: string, brandName: string): string {
    if (this.isGridEdge(brandName)) return content;
    return content.replace(/Grid Edge/g, `${brandName} and Grid Edge`);
  }
}
